<script>
import Vue from 'vue'
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { apiDeviceTokens } from '@modules/crm/api/apiDeviceTokens'
import { aawzDate } from '@utils/dates/aawz-date'
import { copy } from '@utils/Clipboard'
import Title from '@components/section-title'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'

export default {
  components: { Title, FeedbackWrapper },
  data() {
    return {
      busModal: new Vue(),
      tokens: [],
      novoToken: null,
      nomeNovo: '',
      gerandoToken: false,
      revogandoId: null,
      carregandoLista: false,
      headers: [
        { text: 'Nome', value: 'name', sortable: false },
        { text: 'Prefixo', value: 'token_prefix', sortable: false },
        { text: 'Criado em', value: 'created_at', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
    }
  },
  computed: {
    semTokens() {
      return !this.carregandoLista && (!this.tokens || !this.tokens.length)
    },
  },
  created() {
    GlobalBus.$on(MODAL.EXTENSION_TOKEN, this.open)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.EXTENSION_TOKEN, this.open)
  },
  methods: {
    // Reset de estado local do modal — preserva busModal (bus de abertura/
    // fechamento). Chamado como 1ª linha de open() (refactor-rules.md #17).
    clear() {
      this.novoToken = null
      this.nomeNovo = ''
      this.gerandoToken = false
      this.revogandoId = null
      this.carregandoLista = false
    },
    open() {
      this.clear()
      this.busModal.$emit('dialog', true)
      this.carregar()
    },

    async carregar() {
      this.carregandoLista = true
      const r = await apiDeviceTokens.listar()
      if (r && r.status === 200) {
        this.tokens = (r.value && r.value.tokens) || r.value || []
      }
      this.carregandoLista = false
    },

    async gerarToken() {
      if (!this.nomeNovo || !this.nomeNovo.trim()) {
        this.$showSnack({
          text: 'Informe um nome para identificar este token.',
          button: true,
        })
        return
      }
      this.gerandoToken = true
      this.novoToken = null
      const r = await apiDeviceTokens.gerar({ name: this.nomeNovo.trim() })
      this.gerandoToken = false
      if (r && r.status === 200 && r.value) {
        this.novoToken = r.value.token || r.value
        this.nomeNovo = ''
        await this.carregar()
      } else {
        this.$showSnack({
          text: (r && r.message) || 'Erro ao gerar token.',
          button: true,
        })
      }
    },

    confirmarRevogacao(token) {
      this.$showModal({
        type: 'danger',
        v2: true,
        center: true,
        title: 'Revogar token',
        text: `Deseja revogar o token "${
          token.name || 'sem nome'
        }"? Esta ação não pode ser desfeita e a extensão conectada perderá o acesso.`,
        buttons: [
          { text: 'Cancelar', action: () => {} },
          {
            text: 'Revogar',
            action: () => {
              this.revogarToken(token.id)
            },
          },
        ],
      })
    },

    async revogarToken(id) {
      this.revogandoId = id
      const r = await apiDeviceTokens.revogar({ id: id })
      this.revogandoId = null
      if (r && r.status === 200) {
        this.tokens = this.tokens.filter(function (t) {
          return t.id !== id
        })
        this.$showSnack({ text: 'Token revogado com sucesso.', button: false })
      } else {
        this.$showSnack({
          text: (r && r.message) || 'Erro ao revogar token.',
          button: true,
        })
      }
    },

    copiarToken() {
      if (!this.novoToken) return
      copy(this.novoToken).then(() => {
        this.$showSnack({ text: 'Token copiado!', button: false })
      })
    },

    formatarData(val) {
      if (!val) return '—'
      // Backend serializa datas como 'DD/MM/YYYY HH:mm' (day-first, via
      // JSONEncoderExtendedDataFrames). new Date() não parseia day-first
      // ('27/05/2026 ...' vira Invalid Date). aawzDate com o formato explícito
      // resolve; cai pro ISO se o token count divergir.
      const d = aawzDate(val, 'DD/MM/YYYY HH:mm')
      return d && d.isValid() ? d.format('DD/MM/YYYY HH:mm') : String(val)
    },
  },
}
</script>

<template>
  <BaseModal max-width="45%" :bus="busModal" fixedActions>
    <template v-slot:title>
      <Title title="Extensão AAWZ — Tokens de acesso" underlined />
    </template>

    <div class="pa-1">
      <p class="az-text-secondary az-size-14 mb-4">
        Tokens de dispositivo permitem que a extensão Chrome da AAWZ se conecte
        à sua conta sem necessidade de senha. Cada token é exibido apenas uma
        vez no momento da criação.
      </p>

      <!-- Novo token gerado — exibir só uma vez -->
      <div v-if="novoToken" class="mb-5 pa-3 rounded token-callout">
        <div class="d-flex align-center mb-1 az-gap-small">
          <icon-svg
            name="warning-status"
            size="16"
            class="token-callout-icon"
          />
          <span
            class="az-text-primary az-size-12 font-semibold token-callout-text"
          >
            Token gerado — salve agora!
          </span>
        </div>
        <p class="az-text-primary az-size-12 mb-2 token-callout-text">
          Este token só é exibido uma vez. Copie e configure na extensão antes
          de fechar esta janela.
        </p>
        <div class="d-flex align-center az-gap-small">
          <InputByType
            type="text"
            :value="novoToken"
            readonly
            dense
            outlined
            hide-details
            class="flex-grow-1 token-field"
          />
          <BaseButton sm fitContent outline @click="copiarToken">
            <icon-svg name="copy" size="16" class="mr-1" />
            Copiar
          </BaseButton>
        </div>
      </div>

      <!-- Formulário para gerar novo token -->
      <div class="mb-5">
        <p class="az-text-primary az-size-14 font-semibold mb-2"
          >Gerar novo token</p
        >
        <div class="d-flex align-center az-gap-small">
          <InputByType
            v-model="nomeNovo"
            type="text"
            label="Nome do dispositivo (ex: Notebook do trabalho)"
            dense
            outlined
            hide-details
            class="flex-grow-1"
            :disabled="gerandoToken"
            @keyup.enter="gerarToken"
          />
          <BaseButton
            primary
            fitContent
            :loading="gerandoToken"
            :disabled="!nomeNovo || !nomeNovo.trim()"
            @click="gerarToken"
          >
            Gerar
          </BaseButton>
        </div>
      </div>

      <!-- Lista de tokens existentes -->
      <div>
        <FeedbackWrapper
          :loading="carregandoLista"
          :no-data="semTokens"
          message="Nenhum token ativo."
          :min-height="120"
        >
          <BaseTable
            title="Tokens ativos"
            subtitle="Tokens de acesso ativos da extensão AAWZ."
            :headers="headers"
            :items="tokens"
            hide-default-footer
            :items-per-page="-1"
            disableMarginTop
          >
            <template v-slot:item.name="{ item: row }">
              {{ row.name || '—' }}
            </template>

            <template v-slot:item.token_prefix="{ item: row }">
              <code class="token-prefix">{{ row.token_prefix || '—' }}…</code>
            </template>

            <template v-slot:item.created_at="{ item: row }">
              <span class="az-text-secondary az-size-12">
                {{ formatarData(row.created_at) }}
              </span>
            </template>

            <template v-slot:item.actions="{ item: row }">
              <div class="d-flex justify-end">
                <v-progress-circular
                  v-if="revogandoId === row.id"
                  indeterminate
                  size="18"
                  width="2"
                  color="var(--muted)"
                />
                <v-tooltip v-else top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      @click="confirmarRevogacao(row)"
                    >
                      <icon-svg name="delete" size="18" color="var(--muted)" />
                    </v-btn>
                  </template>
                  <span>Revogar token</span>
                </v-tooltip>
              </div>
            </template>
          </BaseTable>
        </FeedbackWrapper>
      </div>
    </div>

    <template v-slot:actions="{ close }">
      <!-- mt-4 no container: margem direta em BaseButton é proibida. -->
      <div class="d-flex align-center justify-end mt-4">
        <BaseButton underlined @click="close">Fechar</BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<style lang="sass" scoped>
// Callout de aviso "salve agora" — paleta âmbar sem token semântico equivalente
// em tokens.json (fallback controlado, cores consumidas uma única vez aqui).
.token-callout
  background: #FFF4CC
  border-left: 4px solid #FFB300

  &-icon,
  &-text
    color: #7A5B00

.token-field
  ::v-deep input
    font-family: monospace
    font-size: 12px

.token-prefix
  font-size: 11px
</style>
